import React, {useRef} from 'react';
import {RefreshControl, StyleSheet} from 'react-native';
import Animated, {LayoutAnimationConfig} from 'react-native-reanimated';
import {useHeaderHeight} from '@react-navigation/elements';
import {observer} from 'mobx-react';

import PromotionTile from '../../components/articleTile/PromotionTile';
import LoadingImg from '../../components/LoadingImg';
import ContentView from '../../components/ui-controls/layout/ContentView';
import WebFooter from '../../components/WebFooter';
import {useScrollCollapseHandler} from '../../hooks/useScrollCollapse';
import {useTabBarBottomPadding} from '../../hooks/useTabBarBottomPadding';
import {useWebScreenDimensions} from '../../hooks/useWebScreenDimensions';
import {AirtablePromotionItem} from '../../models/airtable';
import {promotionsEnabled, usePromotions} from '../../queries/promotions';
import {useStores} from '../../stores/use-stores';

type PromotionsListScreenBaseProps = {
  promotions: AirtablePromotionItem[];
  testID?: string;
};

const PromotionsListScreenBase = observer(
  ({promotions, testID = 'promotions-list-screen-base'}: PromotionsListScreenBaseProps) => {
    const {shopSelectionStore} = useStores();
    const {refetch} = usePromotions();
    const {isWebDesktopScreen} = useWebScreenDimensions();
    const tabBarPadding = useTabBarBottomPadding({skipFocusReset: true});
    const listRef = useRef(null);
    const {scrollHandler} = useScrollCollapseHandler(listRef);
    const headerHeight = useHeaderHeight();
    const refresh = () => {
      // `refetch` ignores the query's `enabled` flag (verified in
      // @tanstack/query-core 5.100.9, queryObserver.cjs:158-171), so pull-to-
      // refresh is a FOURTH fetch trigger alongside `enabled`,
      // `hasFetchedPromotions` and `ensurePromotions`. It therefore goes through
      // the same `promotionsEnabled` predicate rather than re-implementing half
      // of it: without a store number the middleware 400s, and the gift card
      // store 400s too. `AppRoute.PROMOTIONS` is currently registered only in
      // `liquorStoreAppRoutes`, so the gift-card half is latent — registering
      // those routes in `allStoresAppRoutes` would make it live.
      if (!promotionsEnabled(shopSelectionStore.selectedStore?.store_number)) {
        return;
      }
      void refetch();
    };

    return (
      <ContentView testID={testID}>
        {!!shopSelectionStore.storeLoading && <LoadingImg />}
        {!shopSelectionStore.storeLoading && (
          <>
            <Animated.ScrollView
              onScroll={scrollHandler}
              scrollEventThrottle={16}
              refreshControl={<RefreshControl refreshing={false} onRefresh={refresh} />}
              showsVerticalScrollIndicator={isWebDesktopScreen}
              showsHorizontalScrollIndicator={isWebDesktopScreen}
              contentContainerStyle={{
                ...styles.main,
                paddingTop: headerHeight,
                paddingBottom: tabBarPadding,
              }}
              ref={listRef}>
              <LayoutAnimationConfig skipEntering>
                {promotions.map(promotion => (
                  <PromotionTile promotion={promotion} key={promotion.id} />
                )) || null}
              </LayoutAnimationConfig>
              {/* TODO: The WebFooter should be in a global/common place */}
              {!!isWebDesktopScreen && <WebFooter />}
            </Animated.ScrollView>
          </>
        )}
      </ContentView>
    );
  },
);

export default PromotionsListScreenBase;

const styles = StyleSheet.create({
  main: {
    backgroundColor: 'white',
    overflow: 'visible',
  },
});
